import { RefreshCw, MessageSquare, Lock, Unlock, BadgeCheck, ArrowRightLeft, Trash2, AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Admin > Teams table. Extracted from Admin.jsx (Iter48).
 * Presentational — every mutation goes through a callback passed from the parent.
 */
export default function AdminTeamsTable({
  approvedTeams,
  declinedTeams,
  players,
  onRecomputeStandings,
  onDiscordSyncAll,
  onRepRoleSyncAll,
  onLockAll,
  onUnlockAll,
  onVerifyAllTeams,
  onDiscordSyncTeam,
  onSetRepRole,
  onSwitchTier,
  onToggleRosterLock,
  onVerifyAllPlayers,
  onDelete,
}) {
  return (
    <section className="space-y-2" data-testid="admin-teams">
      <div className="flex items-center justify-between px-1 flex-wrap gap-2">
        <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase">
          Approved teams · {approvedTeams.length}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {approvedTeams.length > 0 && (
            <>
              <button
                onClick={onRecomputeStandings}
                data-testid="admin-recompute-standings"
                className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold"
                title="Zero W/L/points then replay every completed fixture with the current rule"
              >
                <RefreshCw className="w-3 h-3" /> Recompute standings
              </button>
              <button
                onClick={onDiscordSyncAll}
                data-testid="admin-discord-sync-all"
                className="text-[10px] font-mono uppercase tracking-widest text-[#5865F2] border border-[#5865F2] hover:bg-[#5865F2] hover:text-white px-3 py-1.5 flex items-center gap-1 font-bold"
                title="Provision Discord role + rep role + private channel for every approved team (idempotent)"
              >
                <MessageSquare className="w-3 h-3" /> Discord sync all
              </button>
              <button
                onClick={onRepRoleSyncAll}
                data-testid="admin-rep-role-sync-all"
                className="text-[10px] font-mono uppercase tracking-widest text-[#5865F2] border border-[#5865F2] hover:bg-[#5865F2] hover:text-white px-3 py-1.5 flex items-center gap-1 font-bold"
                title="Re-assigns each team's Discord rep role to every linked rep (cheap · no channel/role creation)"
              >
                <AtSign className="w-3 h-3" /> Sync rep roles
              </button>
              <button
                onClick={onLockAll}
                data-testid="admin-lock-all-rosters"
                className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border border-[#F4D03F] hover:bg-[#F4D03F] hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold"
              >
                <Lock className="w-3 h-3" /> Lock all rosters
              </button>
              <button
                onClick={onUnlockAll}
                data-testid="admin-unlock-all-rosters"
                className="text-[10px] font-mono uppercase tracking-widest text-gray-400 border border-[#232330] hover:border-cyan-400 hover:text-cyan-400 px-3 py-1.5 flex items-center gap-1"
              >
                <Unlock className="w-3 h-3" /> Unlock all
              </button>
              <button
                onClick={onVerifyAllTeams}
                data-testid="admin-verify-all-teams"
                className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold"
              >
                <BadgeCheck className="w-3 h-3" /> Verify all teams
              </button>
            </>
          )}
        </div>
      </div>
      {approvedTeams.map((t) => {
        const roster = players.filter((p) => p.team_id === t.id);
        const verifiedCount = roster.filter((p) => p.hll_verified).length;
        const total = roster.length;
        const allDone = total > 0 && verifiedCount === total;
        const tint = total === 0
          ? "text-gray-500 border-[#232330]"
          : allDone
            ? "text-cyan-400 border-cyan-400/40"
            : verifiedCount === 0
              ? "text-[#FF007F] border-[#FF007F]/40"
              : "text-[#F4D03F] border-[#F4D03F]/40";
        return (
          <div key={t.id} data-testid={`team-row-${t.tag}`} className="border border-[#232330] bg-[#101015] p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderLeft: `4px solid ${t.logo_color}` }}>
            <div>
              <div className="font-heading font-black text-lg uppercase tracking-tight" style={{ color: t.logo_color }}>{t.name}</div>
              <div className="text-xs text-gray-500 font-mono">
                {t.tag} · {t.tier || "Mains"} · cap {t.roster_cap || 150} · {t.manager} · W{t.wins || 0}-L{t.losses || 0} · {t.points || 0} pts
              </div>
              {total > (t.roster_cap || 150) && (
                <div
                  data-testid={`team-overcap-${t.tag}`}
                  className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#FF007F] border border-[#FF007F]/40 bg-[#FF007F]/10 px-2 py-0.5 inline-block"
                >
                  ⚠ Roster over cap by {total - (t.roster_cap || 150)}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              {t.discord_channel_id && (
                <span
                  data-testid={`team-discord-synced-${t.tag}`}
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[#5865F2] text-[#5865F2] bg-[#5865F2]/10 flex items-center gap-1"
                  title={t.discord_synced_at ? `Synced ${new Date(t.discord_synced_at).toLocaleString()}` : "Discord provisioned"}
                >
                  <MessageSquare className="w-3 h-3" /> DISCORD
                </span>
              )}
              <button
                onClick={() => onDiscordSyncTeam(t)}
                data-testid={`team-discord-sync-${t.tag}`}
                className="text-[10px] font-mono uppercase tracking-widest text-[#5865F2] border border-[#5865F2] hover:bg-[#5865F2] hover:text-white px-3 py-1.5 flex items-center gap-1 font-bold"
                title={t.discord_channel_id ? "Re-run Discord sync (idempotent — reuses existing role/channel IDs)" : "Create Discord role + rep role + private channel for this team"}
              >
                <MessageSquare className="w-3 h-3" /> {t.discord_channel_id ? "Re-sync" : "Discord sync"}
              </button>
              <button
                onClick={() => onSetRepRole && onSetRepRole(t)}
                data-testid={`team-rep-role-${t.tag}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 font-bold border ${
                  t.discord_rep_role_id
                    ? "border-[#5865F2] text-[#5865F2] hover:bg-[#5865F2] hover:text-white"
                    : "border-[#232330] text-gray-400 hover:border-[#5865F2] hover:text-[#5865F2]"
                }`}
                title={t.discord_rep_role_id ? `Rep role: ${t.discord_rep_role_id} — click to change` : "No rep role yet — click to paste a Discord role ID (used to ping reps in match threads)"}
              >
                <AtSign className="w-3 h-3" /> {t.discord_rep_role_id ? "Rep role" : "Set rep role"}
              </button>
              <button
                onClick={() => onSwitchTier(t)}
                data-testid={`team-switch-tier-${t.tag}`}
                className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold"
                title={`Flip to ${t.tier === "Mains" ? "Lite (cap 100)" : "Mains (cap 150)"}`}
              >
                <ArrowRightLeft className="w-3 h-3" /> {t.tier === "Mains" ? "→ Lite" : "→ Mains"}
              </button>
              {t.roster_locked && (
                <span
                  data-testid={`team-roster-lock-status-${t.tag}`}
                  className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-[#F4D03F] text-[#F4D03F] bg-[#F4D03F]/10 flex items-center gap-1"
                  title={t.roster_locked_at ? `Locked ${new Date(t.roster_locked_at).toLocaleString()} by ${t.roster_locked_by || "admin"}` : "Roster locked"}
                >
                  <Lock className="w-3 h-3" /> LOCKED
                </span>
              )}
              <span
                data-testid={`team-verify-count-${t.tag}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${tint}`}
                title={`${verifiedCount}/${total} HLL-verified`}
              >
                {allDone ? <BadgeCheck className="w-3 h-3 inline mr-1" /> : null}
                {verifiedCount}/{total} verified
              </span>
              <button
                onClick={() => onToggleRosterLock(t)}
                data-testid={`team-roster-lock-${t.tag}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 flex items-center gap-1 font-bold border ${
                  t.roster_locked
                    ? "border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black"
                    : "border-[#F4D03F] text-[#F4D03F] hover:bg-[#F4D03F] hover:text-black"
                }`}
              >
                {t.roster_locked ? <><Unlock className="w-3 h-3" /> Unlock roster</> : <><Lock className="w-3 h-3" /> Lock roster</>}
              </button>
              <button
                onClick={() => onVerifyAllPlayers(t)}
                disabled={total === 0 || allDone}
                data-testid={`verify-all-${t.tag}`}
                className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5 flex items-center gap-1 font-bold disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-cyan-400"
              >
                <BadgeCheck className="w-3 h-3" /> Verify all
              </button>
              <Button size="sm" onClick={() => onDelete(t)} data-testid={`delete-team-${t.tag}`}
                      variant="outline"
                      className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs">
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </Button>
            </div>
          </div>
        );
      })}
      {approvedTeams.length === 0 && (
        <div className="border border-[#232330] p-8 text-center text-gray-500 font-mono text-xs">No approved teams yet.</div>
      )}
      {declinedTeams.length > 0 && (
        <>
          <div className="text-[10px] font-mono tracking-widest text-gray-500 uppercase px-1 pt-6">
            Declined · {declinedTeams.length}
          </div>
          {declinedTeams.map((t) => (
            <div key={t.id} className="border border-[#232330] bg-[#0a0a12] p-4 flex flex-wrap items-center justify-between gap-3 opacity-60">
              <div>
                <div className="font-heading font-bold uppercase tracking-tight text-gray-400">{t.name}</div>
                <div className="text-xs text-gray-600 font-mono">{t.tag} · reason: {t.declined_reason || "—"}</div>
              </div>
              <Button size="sm" onClick={() => onDelete(t)} variant="outline"
                      className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs">
                <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
              </Button>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
